import { AspectRatio, Flex, Image, Text } from "@mantine/core";
import { TFunction } from "i18next";
import { Dispatch, SetStateAction } from "react";

import excellentDark from "../../../../assets/illustrations/conditions/excellent-dark.svg";
import excellent from "../../../../assets/illustrations/conditions/excellent.svg";
import fairDark from "../../../../assets/illustrations/conditions/fair-dark.svg";
import fair from "../../../../assets/illustrations/conditions/fair.svg";
import goodDark from "../../../../assets/illustrations/conditions/good-dark.svg";
import good from "../../../../assets/illustrations/conditions/good.svg";
import superbDark from "../../../../assets/illustrations/conditions/superb-dark.svg";
import superb from "../../../../assets/illustrations/conditions/superb.svg";
import { DESCRIPTION_MAX_LENGTH } from "../AuctionDescription";

import { calculateEstimate } from "@/components/elements/AuctionCarCard/utils";
import { formatCurrency } from "@/utils/formatEuro";
import { getListingPriceLabel, shouldHideTransactionPrice } from "@/utils/transactionRecord";

import type { AuctionCar, AuctionCarTransaction } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { JSX } from "react/jsx-runtime";

export function getRenderPrice(
  currentTransaction: AuctionCarTransaction,
  notAvailable: string,
  currency: Currency
) {
  return (value?: number) => {
    if (shouldHideTransactionPrice(currentTransaction!, value)) {
      return (
        <Text fz="sm" c="dimmed">
          {notAvailable}
        </Text>
      );
    }

    return (
      <Text fz="sm" fw={500}>
        {formatCurrency(value!, currency.code)}
      </Text>
    );
  };
}

export function getRenderListingPrice(
  t: TFunction<"translation", undefined>,
  currentTransaction: AuctionCarTransaction,
  renderPrice: (value?: number) => JSX.Element
) {
  return () => {
    const listingPriceLabel = getListingPriceLabel(
      t,
      currentTransaction!,
      currentTransaction!.listing_price
    );

    if (listingPriceLabel) {
      return (
        <Text fz="sm" c="dimmed">
          {listingPriceLabel}
        </Text>
      );
    }

    return renderPrice(currentTransaction!.listing_price);
  };
}

export function getRenderEstimate(
  auctionCar: AuctionCar | undefined,
  currency: Currency,
  renderListingPrice: () => JSX.Element
) {
  return () => {
    if (auctionCar) {
      const estimate = calculateEstimate(auctionCar, currency);

      if (estimate !== "-") {
        return (
          <Text fz="sm" fw={500}>
            {estimate}
          </Text>
        );
      }
    }

    return renderListingPrice();
  };
}

export function getRenderCondition(t: TFunction<"translation", undefined>, isDark: boolean) {
  return (conditionVal?: any) => {
    const cond = String(conditionVal).toLowerCase();

    if (cond === "not_available" || cond === "-1") {
      return (
        <Flex align="center" style={{ width: "100%", height: "100%" }}>
          <Text fz="sm">{String(t(`car.conditions.not_available`, "not_available"))}</Text>
        </Flex>
      );
    }

    const imgSrc =
      cond === "superb"
        ? isDark
          ? superbDark
          : superb
        : cond === "excellent"
          ? isDark
            ? excellentDark
            : excellent
          : cond === "good"
            ? isDark
              ? goodDark
              : good
            : cond === "fair"
              ? isDark
                ? fairDark
                : fair
              : undefined;

    if (!imgSrc) {
      return (
        <Flex align="center" style={{ width: "100%", height: "100%" }}>
          <Text tt="uppercase">{String(t(`car.conditions.${conditionVal}`, conditionVal))}</Text>
        </Flex>
      );
    }

    return (
      <Flex direction="row" justify="space-between" align="center" gap="md" w="fit">
        <Text fz="sm" miw={0} style={{ flex: 1 }} truncate>
          {String(t(`car.conditions.${conditionVal}`, conditionVal))}
        </Text>
        <AspectRatio
          w="2em"
          style={{ display: "flex", alignItems: "center" }}
          pos="relative"
          bottom={2}
        >
          <Image
            src={imgSrc}
            fit="contain"
            alt={String(t(`car.conditions.${cond}`, cond))}
            aria-label={String(t(`car.conditions.${cond}`, cond))}
            style={{ display: "block", margin: "auto" }}
          />
        </AspectRatio>
      </Flex>
    );
  };
}

export function getRenderDescription(
  descriptionExpanded: boolean,
  textColor: string,
  setDescriptionExpanded: Dispatch<SetStateAction<boolean>>,
  t: TFunction<"translation", undefined>
) {
  return (description: string) => {
    const isLongDescription = description.length > DESCRIPTION_MAX_LENGTH;
    const truncatedDescription = isLongDescription
      ? description.slice(0, DESCRIPTION_MAX_LENGTH).replace(/\s+\S*$/, "")
      : description;
    const visibleDescription = descriptionExpanded ? description : truncatedDescription;

    return (
      <Text fz="0.875em" lh={1.6} c={textColor} component="div">
        {visibleDescription}
        {isLongDescription && !descriptionExpanded && (
          <>
            {"... "}
            <Text
              component="button"
              type="button"
              c="primary.7"
              fw={600}
              fz="0.875em"
              tt="uppercase"
              lh={1.6}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "inline",
              }}
              onClick={() => setDescriptionExpanded(true)}
            >
              {t("components.AuctionDescription.read_more")}
            </Text>
          </>
        )}
        {isLongDescription && descriptionExpanded && (
          <>
            {" "}
            <Text
              component="button"
              type="button"
              c="primary.7"
              fw={700}
              fz="0.875em"
              tt="uppercase"
              lh={1.6}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "inline",
              }}
              onClick={() => setDescriptionExpanded(false)}
            >
              {t("components.AuctionDescription.read_less")}
            </Text>
          </>
        )}
      </Text>
    );
  };
}
